import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  CatalogProduct,
  CatalogVariant,
  VocabularyAttribute,
} from '../../../types/catalog';
import {
  ChooserSelection,
  buildChooserAxes,
  optionStates,
  resolveVariant,
} from '../../../utils/catalog/variantChooser';
import { priceRangeLabel } from '../../../utils/catalog/catalogFilters';
import { formatDollarAmount } from '../../../utils/global/helper';

interface Props {
  product: CatalogProduct;
  variants: CatalogVariant[];
  attributes: VocabularyAttribute[];
  initialSelection?: ChooserSelection;
  onBack: () => void;
  onAdd: (variant: CatalogVariant, quantity: number) => void;
}

/** One section per axis; the combination picks the variant, like Square's variation sheet. */
export default function VariantChooser({
  product,
  variants,
  attributes,
  initialSelection,
  onBack,
  onAdd,
}: Props) {
  const theme = useTheme();
  const axes = React.useMemo(
    () => buildChooserAxes(variants, attributes),
    [variants, attributes],
  );
  const [selection, setSelection] = React.useState<ChooserSelection>(
    initialSelection ?? {},
  );
  const [quantity, setQuantity] = React.useState(1);
  const resolved = resolveVariant(variants, axes, selection);
  const priceLabel = resolved
    ? formatDollarAmount(resolved.price * quantity)
    : priceRangeLabel(variants, amount => formatDollarAmount(amount));

  const toggle = (axisKey: string, value: string) =>
    setSelection(current =>
      current[axisKey] === value
        ? Object.fromEntries(
            Object.entries(current).filter(([k]) => k !== axisKey),
          )
        : { ...current, [axisKey]: value },
    );

  return (
    <Stack gap={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton size="small" onClick={onBack} aria-label="Back to items">
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
        <Box minWidth={0} flexGrow={1}>
          <Typography variant="h5" noWrap>
            {product.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[product.brand?.name, priceLabel].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          <IconButton
            size="small"
            disabled={quantity <= 1}
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
            aria-label="Decrease quantity">
            <RemoveRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" minWidth={20} textAlign="center">
            {quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setQuantity(q => q + 1)}
            aria-label="Increase quantity">
            <AddRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Button
          variant="contained"
          disabled={!resolved}
          onClick={() => resolved && onAdd(resolved, quantity)}>
          Add
        </Button>
      </Box>

      {axes.map(axis => (
        <Box key={axis.key}>
          <Typography variant="overline" color="text.secondary">
            {axis.label}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
              gap: 1,
            }}>
            {optionStates(variants, axes, axis, selection).map(option => {
              const isSelected = selection[axis.key] === option.value;
              return (
                <ButtonBase
                  key={option.value}
                  disabled={!option.available}
                  onClick={() => toggle(axis.key, option.value)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '10px',
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'accent.main' : 'background.paper',
                    opacity: option.available ? 1 : 0.4,
                    textAlign: 'left',
                    fontFamily: theme.typography.fontFamily,
                    '&:hover': { bgcolor: 'accent.main' },
                  }}>
                  <Typography variant="subtitle2" noWrap>
                    {option.value}
                  </Typography>
                  {option.price !== null && !resolved && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      flexShrink={0}>
                      {formatDollarAmount(option.price)}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}

      {!axes.length && (
        <Typography variant="body2" color="text.secondary">
          This item has no options.
        </Typography>
      )}
    </Stack>
  );
}
